import React, {useState, useContext} from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import {ASH, CHARCOAL, FOCUS_BLUE, TARMAC, WHITE} from '../../../shared/style/colors';
import FollowCompanyButton from '../../../shared/library/buttons/follow/follow-company-button';
import GhostButton from '../../../shared/library/buttons/base/ghost';
import PillLabel from '../../../shared/library/buttons/base/pill-label';
import Heading from '../../../shared/library/typography/heading';
import SubTitleBase from '../../../shared/library/typography/subtitle';
import {BASE_TEXT, FONT_FAMILY} from '../../../shared/style/typography';
import Links from '../../../shared/library/links/links2';
import Tags from '../../../shared/library/tags';
import VerfiedIcon from '../../../shared/library/icons/verified.svg';
import MenuPopover from '../../../shared/library/popovers/menu';
import {
  ADD_STACK,
  CLAIM_THIS_PROFILE,
  EDIT_PROFILE,
  JOIN_COMPANY,
  MANAGE_MEMBERS,
  MANAGE_APPLICATIONS,
  MANAGE_TEAM,
  SETTINGS_ICONS,
  SETTINGS_LABELS,
  VIEW_PLAN,
  PAYMENT_OPTIONS
} from '../../../shared/constants/settings';
import {redirectTo} from '../../../shared/utils/navigation';
import {MobileContext} from '../../../shared/enhancers/mobile-enhancer';
import {PHONE} from '../../../shared/style/breakpoints';
import {hasFeature} from '../../../shared/utils/has-feature';
import {
  FEATURE_PREMIUM_COMPANY_PROFILE,
  STACK_APPLICATIONS
} from '../../../shared/constants/features';
import {useSendAnalyticsEvent} from '../../../shared/enhancers/analytics-enhancer';
import {truncateText} from '../../../shared/utils/truncate-text';
import {NavigationContext} from '../../../shared/enhancers/router-enhancer';
import {getCurrentPlan} from './plan-modal';
import {COMPANY} from '../../../shared/constants/analytics';
import {PrivateModeContext} from './../../../shared/enhancers/private-mode-enchancer';
import {TOP} from '../../../shared/constants/placements';
import PopoverWithAnchor from '../../../shared/library/popovers/base-v2';
import {featurePermissionCheck} from '../../../shared/utils/feature-permission-check';
import OrganizationPicker from '../../../shared/library/organization-picker';
import {
  ORG_STRUCTURE_MODES,
  OrganizationContext
} from '../../../shared/enhancers/organization-enhancer';

const SubTitle = glamorous(SubTitleBase)({fontSize: 14, fontFamily: FONT_FAMILY});

const Container = glamorous.div({
  gridArea: 'sidebar',
  background: WHITE,
  paddingLeft: 43,
  paddingRight: 43,
  marginLeft: -33,
  borderLeft: '1px solid #ebebeb',
  borderRight: '1px solid #ebebeb',
  [PHONE]: {
    border: 0,
    paddingLeft: 0,
    paddingRight: 0,
    width: '100vw',
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0
  }
});

const Sticky = glamorous.div(
  {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    [PHONE]: {
      minHeight: 'inherit',
      position: 'relative',
      paddingLeft: 10,
      paddingRight: 10,
      alignItems: 'center',
      paddingBottom: 0,
      paddingTop: 10
    }
  },
  ({overlapBg}) => ({
    top: overlapBg ? 60 : 0,
    [PHONE]: {
      top: overlapBg ? '-60px' : 0,
      marginBottom: overlapBg ? '-60px' : 0
    }
  })
);

const Logo = glamorous.img(
  {
    width: 115,
    height: 115,
    borderRadius: 4,
    marginBottom: 18,
    backgroundColor: WHITE,
    border: '1px solid #ebebeb'
  },
  ({overlapBg}) => ({
    marginTop: overlapBg ? -38 : 38,
    [PHONE]: {
      marginTop: 20
    }
  })
);

const Spacer = glamorous.div({
  height: 25
});

const Followers = glamorous.div({
  ...BASE_TEXT,
  color: CHARCOAL,
  lineHeight: 1.08,
  marginTop: 6
});

const Description = glamorous.div({
  ...BASE_TEXT,
  color: TARMAC,
  fontSize: 13,
  lineHeight: 1.5,
  marginTop: 22,
  [PHONE]: {
    textAlign: 'center'
  }
});

const ActionButtons = glamorous.div({
  display: 'flex',
  marginTop: 22,
  marginBottom: 32,
  [PHONE]: {
    width: '100%'
  },
  '& button ~ button': {
    marginLeft: 10
  },
  '>button': {
    height: 40,
    [PHONE]: {
      flexGrow: 1,
      width: '50%'
    }
  }
});

const HRule = glamorous.div({
  width: '100%',
  borderBottom: `1px solid ${ASH}`,
  marginTop: 20,
  marginBottom: 20
});

const Tools = glamorous.div({
  display: 'flex',
  flexWrap: 'wrap',
  marginTop: 10
});

const Tool = glamorous.a({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  alignContent: 'center',
  marginRight: 10,
  marginBottom: 10,
  ...BASE_TEXT,
  fontSize: 11,
  color: TARMAC,
  textDecoration: 'none',
  cursor: 'pointer'
});

const ToolName = glamorous.h3({
  overflow: 'hidden',
  margin: 0,
  marginTop: 0,
  width: 50,
  fontSize: 11,
  textAlign: 'center',
  lineHeight: 1.2
});

const StyledVerfiedIcon = glamorous(VerfiedIcon)({
  marginLeft: 5,
  width: 15,
  height: 15
});

const SeeMore = glamorous.a({
  ...BASE_TEXT,
  color: CHARCOAL,
  fontSize: 13,
  textDecoration: 'underline'
});

const MenuWrapper = glamorous.div(
  {
    position: 'relative',
    display: 'flex',
    [PHONE]: {
      width: '100%',
      justifyContent: 'center'
    }
  },
  ({overlapBg}) => ({
    '>:nth-child(2)': {
      marginTop: overlapBg ? 21 : 78,
      marginLeft: 21,
      [PHONE]: {
        marginTop: 0,
        position: 'absolute',
        top: overlapBg ? 65 : 0,
        right: 0
      }
    }
  })
);

const menuOptions = (
  {canEdit, hasOwner},
  currentPlan,
  showAdminOptions,
  privateMode,
  orgStructureMode
) => {
  const menu = [];

  if (canEdit) {
    menu.push(ADD_STACK);
    if (showAdminOptions) menu.push(EDIT_PROFILE, MANAGE_MEMBERS);
    if (showAdminOptions && privateMode && orgStructureMode !== ORG_STRUCTURE_MODES.flexible)
      menu.push(MANAGE_TEAM);
    if (currentPlan) {
      menu.push(VIEW_PLAN);
    }
  } else if (hasOwner) {
    menu.push(JOIN_COMPANY);
  } else {
    menu.push(CLAIM_THIS_PROFILE);
  }

  if (menu.length === 0) return null;

  if (privateMode && showAdminOptions && featurePermissionCheck(privateMode, STACK_APPLICATIONS)) {
    menu.push(MANAGE_APPLICATIONS);
  }

  if (canEdit && privateMode && showAdminOptions) menu.push(PAYMENT_OPTIONS);

  return menu.map(setting => ({
    id: setting,
    label: SETTINGS_LABELS[setting],
    icon: SETTINGS_ICONS[setting]
  }));
};

const Sidebar = ({company, menu, isAdmin, isSiteAdmin}) => {
  const [followersCount, setFollowersCount] = useState(company.followers.count);
  const [truncateDesc, setTruncateDesc] = useState(true);
  const isMobile = useContext(MobileContext);
  const navigate = useContext(NavigationContext);
  const sendAnalyticsEvent = useSendAnalyticsEvent();
  const privateMode = useContext(PrivateModeContext);
  const {orgStructureMode} = useContext(OrganizationContext);

  const handleMenuClick = option => {
    sendAnalyticsEvent('click_menuOption', {optionName: option});
    switch (option) {
      case EDIT_PROFILE:
        redirectTo(`${company.path}/edit`);
        break;
      case ADD_STACK:
        redirectTo('create-stack/stack-type');
        break;
      case JOIN_COMPANY:
        redirectTo(`become-company-member?company=${company.name}`);
        break;
      case CLAIM_THIS_PROFILE:
        redirectTo(`become-company-member?company=${company.name}`);
        break;
      case MANAGE_APPLICATIONS:
        redirectTo(`${company.path}/applications`);
        break;
      case MANAGE_MEMBERS:
        redirectTo(`${company.path}/members`);
        break;
      case MANAGE_TEAM:
        redirectTo(`${company.path}/teams`);
        break;
      case VIEW_PLAN:
        navigate(`${company.path}/plan`);
        break;
      case PAYMENT_OPTIONS:
        redirectTo(`/enterprise#pricing`);
        break;
    }
  };

  const options = menuOptions(
    menu,
    getCurrentPlan(company.plans),
    isAdmin || isSiteAdmin,
    privateMode,
    orgStructureMode,
    company
  );
  const overlapBg = hasFeature(company, FEATURE_PREMIUM_COMPANY_PROFILE) && company.bannerImageUrl;
  return (
    <Container>
      <Sticky overlapBg={overlapBg}>
        <MenuWrapper overlapBg={overlapBg}>
          <Logo src={company.imageUrl} alt={company.name} overlapBg={overlapBg} />
          {options && <MenuPopover options={options} onClick={handleMenuClick} />}
        </MenuWrapper>
        {company.amIOwner && company.privateMode && (
          <OrganizationPicker customStyle={{marginBottom: 25}} />
        )}
        <Heading>
          {company.name}
          {company.verified && <StyledVerfiedIcon />}
        </Heading>
        <Followers>
          {followersCount === 0 ? 'No' : followersCount} Follower{followersCount !== 1 && 's'}
        </Followers>
        {company.description && (
          <Description>
            {truncateDesc ? truncateText(company.description, 120, '... ') : company.description}
            {truncateDesc && company.description.length > 120 && (
              <SeeMore
                href="#"
                onClick={event => {
                  event.preventDefault();
                  setTruncateDesc(false);
                }}
              >
                more
              </SeeMore>
            )}
          </Description>
        )}
        <ActionButtons>
          <FollowCompanyButton
            companyId={company.id}
            following={company.following}
            onToggle={newFollowersCount => {
              sendAnalyticsEvent('click_followButton', {
                follow: !company.following,
                followType: COMPANY
              });
              setFollowersCount(newFollowersCount);
            }}
          />
          {company.jobs.count > 0 && isMobile && !privateMode && (
            <GhostButton onClick={() => (window.location = '#jobs')} color={FOCUS_BLUE}>
              View Jobs <PillLabel>{company.jobs.count}</PillLabel>
            </GhostButton>
          )}
        </ActionButtons>
        <Links
          twitter={company.twitterUsername}
          website={company.websiteUrl}
          location={company.location}
          isCompany={true}
          layout={isMobile ? 'row' : 'column'}
          onClick={() => {}}
        />
        {company.tags && company.tags.length > 0 && (
          <>
            {!isMobile && <HRule />}
            <Tags tags={company.tags} />
          </>
        )}
        {company.tools && company.tools.length > 0 && (
          <>
            {!isMobile ? <HRule /> : <Spacer />}
            <SubTitle>Tools by {company.name}</SubTitle>
            <Tools>
              {company.tools.map(t => (
                <PopoverWithAnchor
                  customStyle={{backgroundColor: CHARCOAL, color: WHITE}}
                  arrowColor={CHARCOAL}
                  padding={10}
                  placement={TOP}
                  activateMode="hover"
                  key={t.slug}
                  hidden={true}
                  anchor={
                    <Tool
                      key={t.slug}
                      href={t.canonicalUrl}
                      title={`Visit tool profile for ${t.name}`}
                    >
                      <img alt={t.name} src={t.imageUrl} style={{width: 40, height: 40}} />{' '}
                      <ToolName>{truncateText(t.name, 15, '...')}</ToolName>
                    </Tool>
                  }
                >
                  {t.name}
                </PopoverWithAnchor>
              ))}
            </Tools>
          </>
        )}
      </Sticky>
    </Container>
  );
};

Sidebar.propTypes = {
  company: PropTypes.object.isRequired,
  menu: PropTypes.object,
  isAdmin: PropTypes.bool,
  isSiteAdmin: PropTypes.bool
};

export default Sidebar;
